import * as Sentry from "@sentry/react";

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogOptions {
  tags?: Record<string, string>;
  extra?: Record<string, any>;
}

class Logger {
  private isDevelopment = true; // Always show logs locally
  private performanceMarks: Record<string, number> = {};

  private log(level: LogLevel, message: string | Error, category?: string, options: LogOptions = {}) {
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      level,
      category,
      ...options
    };

    // Development console logging
      const consoleMethod = {
        debug: console.debug,
        info: console.log,
        warn: console.warn,
        error: console.error
      }[level];

      const prefix = `[${timestamp}] [${level.toUpperCase()}]${category ? ` [${category}]` : ''}`;
      const style = {
        debug: 'color: #666',
        info: 'color: #0066cc',
        warn: 'color: #cc8800',
        error: 'color: #cc0000; font-weight: bold'
      }[level];

      consoleMethod(
        `%c${prefix}`,
        style,
        message,
        options.extra && { extra: options.extra },
        options.tags && { tags: options.tags }
      );

    // Production logging to Sentry
    if (level === 'error') {
      Sentry.captureException(message instanceof Error ? message : new Error(String(message)), {
        tags: { category, ...options.tags },
        extra: options.extra
      });
    } else {
      Sentry.addBreadcrumb({
        category,
        message: message instanceof Error ? message.message : String(message),
        level: level === 'warn' ? 'warning' : level,
        data: options.extra
      });
    }
  }

  debug(message: string, category?: string, options?: LogOptions) {
      this.log('debug', message, category, options);
  }

  info(message: string, category?: string, options?: LogOptions) {
    this.log('info', message, category, options);
  }

  warn(message: string, category?: string, options?: LogOptions) {
    this.log('warn', message, category, options);
  }

  error(error: Error | string, category?: string, options?: LogOptions) {
    this.log('error', error, category, options);
  }

  performance = {
    start: (label: string) => {
      this.performanceMarks[label] = performance.now();
    },
    
    end: (label: string) => {
      const startTime = this.performanceMarks[label];
      if (startTime) {
        const duration = performance.now() - startTime;
        delete this.performanceMarks[label];
        
        console.log(
          `%c[PERFORMANCE] ${label}: ${duration.toFixed(2)}ms`,
          'color: #9933cc'
        );
        
        Sentry.addBreadcrumb({
          category: 'performance',
          message: `${label} completed`,
          data: { duration },
          level: 'info'
        });
      }
    }
  };
}

export const logger = new Logger();